import { Box, Typography, Button } from "@mui/material";
import { colors } from "../../../ui/colors";
import { useState, useEffect } from "react";
import { 
  Email, 
  Sms,
  SecurityOutlined
} from "@mui/icons-material";

export const VerifyYourAccount = ({ onMethodSelect, userEmail, userPhone }) => {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleEmailVerification = () => {
    onMethodSelect('email');
  };

  const handleSmsVerification = () => {
    onMethodSelect('phone');
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 480,
        borderRadius: "16px",
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        p: { xs: 4, md: 6 },
        textAlign: "center",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
        border: "1px solid rgba(251, 142, 0, 0.1)",
        transform: showContent ? "translateY(0)" : "translateY(20px)",
        opacity: showContent ? 1 : 0,
        transition: "all 400ms ease-out",
      }}
    >
      {/* Main icon */}
      <Box
        sx={{
          display: "inline-flex",
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `linear-gradient(135deg, ${colors.orange.base} 0%, ${colors.yellow.base} 100%)`,
            boxShadow: "0 8px 24px rgba(251, 142, 0, 0.25)",
          }}
        >
          <SecurityOutlined
            sx={{
              fontSize: 40,
              color: "white",
            }}
          />
        </Box>
      </Box>

      {/* Title */}
      <Typography
        variant="h3"
        sx={{
          fontWeight: 800,
          fontSize: { xs: "28px", md: "32px" },
          mb: 2,
          color: colors.gray[800],
        }}
      >
        Verify Your Account
      </Typography>

      {/* Subtitle */}
      <Box sx={{ mb: 4 }}>
        <Typography
          sx={{
            color: colors.gray[600],
            fontSize: { xs: "16px", md: "18px" },
            fontWeight: 500,
            lineHeight: 1.6,
            mb: 1,
          }}
        >
          Choose your preferred verification method
        </Typography>
        <Typography
          sx={{
            color: colors.gray[500],
            fontSize: { xs: "15px", md: "16px" },
          }}
        >
          We'll send you a verification code to confirm your identity
        </Typography>
      </Box>

      {/* Verification buttons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          mb: 4,
        }}
      >
        {/* Email button */}
        <Button
          onClick={handleEmailVerification}
          sx={{
            borderRadius: "12px",
            height: "64px",
            px: 4,
            background: "transparent",
            border: `2px solid ${colors.orange[100]}`,
            color: colors.gray[700],
            fontWeight: 600,
            fontSize: "16px",
            textTransform: "none",
            transition: "all 200ms ease-out",
            "&:hover": {
              background: "rgba(251, 142, 0, 0.04)",
              border: `2px solid ${colors.orange.base}`,
              transform: "translateY(-2px)",
              boxShadow: "0 4px 16px rgba(251, 142, 0, 0.15)",
            },
          }}
          variant="outlined"
          disableElevation
        >
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 2,
              width: "100%",
              justifyContent: "flex-start",
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(251, 142, 0, 0.1)",
              }}
            >
              <Email 
                sx={{ 
                  fontSize: 20,
                  color: colors.orange.base,
                }} 
              />
            </Box>
            <Box sx={{ textAlign: "left" }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  color: colors.gray[800],
                }}
              >
                EMAIL
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: colors.gray[500],
                }}
              >
                {userEmail ? `Send to ${userEmail}` : 'Verify via email address'}
              </Typography>
            </Box>
          </Box>
        </Button>

        {/* SMS button */}
        <Button
          onClick={handleSmsVerification}
          sx={{
            borderRadius: "12px",
            height: "64px",
            px: 4,
            background: "transparent",
            border: `2px solid ${colors.orange[100]}`,
            color: colors.gray[700],
            fontWeight: 600,
            fontSize: "16px",
            textTransform: "none",
            transition: "all 200ms ease-out",
            "&:hover": {
              background: "rgba(251, 142, 0, 0.04)",
              border: `2px solid ${colors.orange.base}`,
              transform: "translateY(-2px)",
              boxShadow: "0 4px 16px rgba(251, 142, 0, 0.15)",
            },
          }}
          variant="outlined"
          disableElevation
        >
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 2,
              width: "100%",
              justifyContent: "flex-start",
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(251, 142, 0, 0.1)",
              }}
            >
              <Sms 
                sx={{ 
                  fontSize: 20,
                  color: colors.orange.base,
                }} 
              />
            </Box>
            <Box sx={{ textAlign: "left" }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  color: colors.gray[800],
                }}
              >
                SMS
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: colors.gray[500],
                }}
              >
                {userPhone ? `Send to ${userPhone}` : 'Verify via text message'}
              </Typography>
            </Box>
          </Box>
        </Button>
      </Box>

      {/* Info note
      <Box
        sx={{
          p: 3,
          borderRadius: "8px",
          backgroundColor: "rgba(251, 142, 0, 0.04)",
          border: `1px solid ${colors.orange[100]}`,
        }}
      >
        <Typography
          sx={{
            color: colors.gray[600],
            fontSize: { xs: "14px", md: "15px" },
            lineHeight: 1.5,
          }}
        >
          <strong>Secure verification:</strong> We use industry-standard security measures to protect your account and personal information.
        </Typography>
      </Box> */}
    </Box>
  );
};